import Vue from 'vue'
import Vuex from 'vuex'
import {API} from '@/integrations/axios'
import helpers from '@/utils/helpers'
import VueCookies from 'vue-cookies'

const APP_DOMAIN = document.querySelector('meta[name="app-domain"]').content
const APP_KEY = document.querySelector('meta[name="app-key"]').content
const APP_VER = document.querySelector('meta[name="app-version"]').content
const APP_COLOR = document.querySelector('meta[name="app-color"]').content
const APP_BRANDING = document.querySelector('meta[name="app-branding"]')?.content || 1
const APP_THEME = document.querySelector('meta[name="design-theme"]').content
const API_URL = document.querySelector('meta[name="app-url"]').content
const OAUTH_URL = document.querySelector('meta[name="app-oauth"]').content
const CORE_VER = '1868764225' // For handle core updates independenced of any app instance

console.log({core: CORE_VER, app: APP_VER})

Vue.use(Vuex)
Vue.use(VueCookies)

const requireModules = require.context('./modules', true, /index.js*$/)
const modulesUnparsed = []
const modules = {}

requireModules.keys().forEach(key => {
  modulesUnparsed.push(requireModules(key))
})

modulesUnparsed.forEach(el => {
  Object.keys(el).forEach(key => {
    modules[el[key].namespace] = el[key]
  })
})

const state = {
  APP: {
    _domain: APP_DOMAIN,
    _key: APP_KEY,
    _color: APP_COLOR,
    _branding: APP_BRANDING,
    _theme: APP_THEME,
    _api: API_URL,
    _oauth: OAUTH_URL,
    _bg: Vue.$cookies.get('bg_color') || null,
    _published: Vue.$cookies.get('is_published') || true,
    _name: Vue.$cookies.get('appName') || '',
    _logo: window.localStorage.getItem('appLogo') || '',
    _auth: Vue.$cookies.get('appAuth') || [],
    _locale: Vue.$cookies.get('locale') || 'en_US',
    _time_format: Vue.$cookies.get('timeFormat') || '24H',
    _loaded: false,
    _config_loaded: false,
    _cache: [],
    _history: [],
    _menu_style: Vue.$cookies.get('menu_style') || null
  }
}

const actions = {
  async getAppConfig({commit}) {
    API.get('/user/app-config?expand=app_logo')
      .then(result => {
        if (result.data.is_published === false && !helpers.isPreviewFrame()) {
          commit('setAppState', false)
          return
        } else {
          commit('setAppState', true)
        }
        commit('setThemeColor', result.data.main_color)
        commit('setTimeFormat', result.data.time_format)
        commit('setAppMenuStyle', result.data.menu_style)
        commit('setBgColor', result.data.bg_color)
        commit('setAppProperties', result.data)
        commit('setConfigLoad')
      })
      .catch(err => {
        console.log(err)
        if (err.response.status === 451 || err.response.status === 423) {
          commit('setAppState', false)
          commit('setConfigLoad')
          return
        }
      })
  }
}

const mutations = {
  setAppState(state, data) {
    Vue.$cookies.set('is_published', data)
    state.APP._published = data
  },
  setConfigLoad(state) {
    state.APP._config_loaded = true
  },
  setAppMenuStyle(state, data) {
    Vue.$cookies.set('menu_style', data)
    state.APP._menu_style = data
  },
  setThemeColor(state, color) {
    Vue.$cookies.set('themeColor', color)
    state.APP._color = color
  },
  setTimeFormat(state, time_format) {
    Vue.$cookies.set('timeFormat', time_format)
    state.APP._time_format = time_format
  },
  setBgColor(state, color) {
    Vue.$cookies.set('bgColor', color)
    state.APP._bg = color
  },
  setAppProperties(state, data) {
    Vue.$cookies.set('app_name', data.app_name)
    window.localStorage.setItem('app_logo', data.app_logo)
    Vue.$cookies.set('locale', data.locale)
    Vue.$cookies.set('currency_title', data.currency.title)
    if (data.currency.symbol === '🗲') {
      Vue.$cookies.set('currency_symbol', 'lightning-icon')
    } else {
      Vue.$cookies.set('currency_symbol', data.currency.symbol)
    }
    Vue.$cookies.set('currency_before', data.currency.is_before)
    Vue.$cookies.set('app_auth', JSON.stringify(data.signup_mode))

    state.APP._name = data.app_name
    state.APP._locale = data.locale
    state.APP._logo = data.app_logo
    state.APP._auth = data.signup_mode
  },
  setAppLoadedState(state, data) {
    state.APP._loaded = data
  },
  cacheCurrentPage(state, data) {
    state.APP._cache.push(data)
  },
  updateCachedItem(state, {id, data}) {
    if (!state.APP._cache[state.APP._cache.length - 1]) return
    const keys = Object.keys(data)
    keys.forEach(key => {
      const dataKey = Object.keys(state.APP._cache[state.APP._cache.length - 1].data)[0]
      const item = state.APP._cache[state.APP._cache.length - 1].data[dataKey].find(el => {
        return String(el.id) === String(id)
      })

      if (item) item[key] = data[key]
    })
  },
  deleteCachedItem(state, {id}) {
    if (!state.APP._cache[0]) return
    const dataKey = Object.keys(state.APP._cache[0].data)[0]
    delete state.APP._cache[0].data[dataKey].find(el => {
      return el.id === id
    })
  },
  popCache(state) {
    state.APP._cache.pop()
  },
  purgeCache(state) {
    state.APP._cache = []
  },
  pushHistory(state, data) {
    state.APP._history.push(data)
  },
  popHistory(state) {
    state.APP._history.pop()
  }
}

const getters = {
  themeColorRGBA: state => {
    let color
    let stateColor = state.APP._color
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(stateColor)) {
      color = stateColor.substring(1).split('')
      if (color.length == 3) {
        color = [color[0], color[0], color[1], color[1], color[2], color[2]]
      }
      color = '0x' + color.join('')
      return {r: (color >> 16) & 255, g: (color >> 8) & 255, b: color & 255, a: 1}
    }
  },
  menuStyle: state => state.APP._menu_style,
  themeColorHEX: state => state.APP._color,
  theme: state => state.APP._theme,
  appDomain: state => state.APP._domain,
  appKey: state => state.APP._key,
  appApi: state => state.APP._api,
  appOauth: state => state.APP._oauth,
  appBg: state => state.APP._bg,
  appPublished: state => state.APP._published,
  appName: state => state.APP._name,
  appLogo: state => state.APP._logo,
  appBranding: state => state.APP._branding,
  appAuth: state => state.APP._auth,
  appLocale: state => state.APP._locale,
  appTimeFormat: state => state.APP._time_format,
  appLoaded: state => {
    return new Promise(resolve => {
      const interval = setInterval(() => {
        if (state.APP._loaded) {
          clearInterval(interval)
          resolve()
        }
      }, 333)
    })
  },
  configLoaded: state => {
    return new Promise(resolve => {
      const interval = setInterval(() => {
        if (state.APP._domain && state.APP._key && state.APP._color && state.APP._theme && state.APP._api && state.APP._oauth) {
          clearInterval(interval)
          resolve()
        }
      }, 55)
    })
  },
  prefetchDone: state => {
    return new Promise(resolve => {
      const interval = setInterval(() => {
        if (state.APP._config_loaded) {
          clearInterval(interval)
          resolve()
        }
      }, 55)
    })
  },
  cache: state => state.APP._cache[state.APP._cache.length - 1],
  history: state => state.APP._history
}

export const store = new Vuex.Store({
  state,
  mutations,
  getters,
  actions,
  modules,
  strict: process.env.NODE_ENV !== 'production'
})
